import React from "react";
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent } from "../ui/card";
import { FactorCards } from "./Factors";
import { Mini, Overline, StockHeader, ThesisStrip } from "./Shared";
import { baseRates as B, fmtPct } from "./data";

const TT = { background: "#18181b", border: "1px solid #27272a", fontSize: 11 };

const OddsChart = () => {
  const m = (v) => `${v < 0 ? "−" : ""}${Math.abs(v)}`;
  const data = B.hist.map((h) => ({
    ...h,
    label: h.lo === 35 ? "≥35" : h.lo === -40 ? "<−35" : m(h.lo),
    range: h.lo === 35 ? "≥ +35%" : h.lo === -40 ? "< −35%" : `${m(h.lo)}% to ${m(h.lo + 5)}%`,
  }));
  return (
    <div className="h-64" data-testid="kfm-odds-histogram-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 22, right: 8, bottom: 4, left: -18 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="#27272a" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#71717a" }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 10, fill: "#71717a" }} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={TT} formatter={(v) => [`${v} cohorts`, "count"]} labelFormatter={(l, p) => p?.[0]?.payload?.range || l} />
          <ReferenceLine x="0" stroke="#a1a1aa" strokeDasharray="3 3" label={{ value: "sector", position: "top", fontSize: 10, fill: "#a1a1aa" }} />
          <Bar dataKey="n" radius={[2, 2, 0, 0]}>
            {data.map((d) => <Cell key={d.lo} fill={d.lo >= 0 ? "#34d399" : "#f87171"} fillOpacity={d.lo >= -5 && d.lo <= 15 ? 0.95 : 0.55} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const BandLadder = () => (
  <div className="h-40" data-testid="kfm-odds-band-ladder">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={B.bands} margin={{ top: 14, right: 4, bottom: 0, left: -24 }}>
        <XAxis dataKey="band" tick={{ fontSize: 10, fill: "#71717a" }} tickLine={false} axisLine={false} />
        <YAxis tick={{ fontSize: 10, fill: "#71717a" }} tickLine={false} axisLine={false} />
        <Tooltip contentStyle={TT} formatter={(v, k, p) => [`${v}% mean · ${p.payload.hit}% hit · n ${p.payload.n}`, "6m vs peers"]} />
        <Bar dataKey="mean" radius={[2, 2, 0, 0]} label={{ position: "top", fontSize: 9, fill: "#a1a1aa", formatter: (v) => Number(v).toFixed(1) }}>
          {B.bands.map((b) => <Cell key={b.band} fill={b.band === "S8" ? "#38bdf8" : "#3f3f46"} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export default function VariantOdds() {
  return (
    <div className="space-y-4" data-testid="kfm-variant-a">
      <StockHeader compact />
      <Card data-testid="kfm-variant-a-hero"><CardContent className="pt-5 pb-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <div>
            <Overline>Odds of this setup — what happened to cohorts like DELL's</Overline>
            <div className="text-sm mt-1">{B.profile} · <span className="font-mono">{B.n}</span> historical cohorts · {B.period}</div>
          </div>
          <span className="text-[10px] font-mono px-2 py-1 rounded border border-sky-500/40 text-sky-300 bg-sky-500/10">REAL BASE RATES</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <OddsChart />
            <div className="text-[11px] text-muted-foreground mt-1">x = 6-month (126-session) return vs sector × market-cap median peers, % · bars = number of cohorts</div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Mini testid="kfm-odds-stat-hit-rate" k="Beat peers" v={`${B.hit}%`} accent="text-emerald-400" />
              <Mini testid="kfm-odds-stat-median" k="Median outcome" v={fmtPct(B.median)} />
              <Mini k="Average outcome" v={fmtPct(B.mean)} />
              <Mini testid="kfm-odds-stat-iqr" k="Middle half" v={`${fmtPct(B.p25)} … ${fmtPct(B.p75)}`} />
              <Mini k="Worst 10%" v={`≤ ${fmtPct(B.p10)}`} accent="text-red-400" />
              <Mini k="Best 10%" v={`≥ ${fmtPct(B.p90)}`} />
            </div>
            <p className="text-[11px] text-foreground/80 leading-relaxed border-l-2 border-sky-500/50 pl-3">
              A bit more than half (<b>{Math.round(B.hit)} in 100</b>) of setups like this beat their peers over six months; the average is
              pulled up by a long right tail, so the typical outcome is modest. A base rate for this narrow profile, not a forecast for DELL.
            </p>
          </div>
        </div>
        <div className="mt-5 pt-4 border-t border-border/50">
          <Overline className="mb-1">Where S8 sits — mean 6-month outcome by V3 band (all cohorts)</Overline>
          <BandLadder />
          <div className="text-[11px] text-muted-foreground">Edge is concentrated in the top band (S10); S6–S9 are modestly positive — the reason S6–9 is used as a go/no-go gate, not a sizing signal.</div>
        </div>
      </CardContent></Card>
      <ThesisStrip />
      <FactorCards prefix="kfm-a" />
    </div>
  );
}
